import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Known page slugs handled by the monolith. Anything else falls back to
 * "dashboard". When NutriMart.tsx is split, each entry will become its own
 * <Route> in App.tsx and this hook will go away.
 */
export const KNOWN_PAGES = new Set<string>([
  "dashboard", "products", "detail", "aiChat", "nutricoach",
  "scanner", "tracker", "rewards", "referral", "wearable", "community",
  "aiAgent", "healthReport", "bodyScan", "learn", "notifications",
  "checkout", "orders", "favorites", "profile", "compare", "stores",
  "admin", "vendor", "myaccount",
  "healthGoals",
]);

export const slugFromPath = (pathname: string): string => {
  const seg = pathname.replace(/^\/+|\/+$/g, "").split("/")[0] || "";
  if (!seg) return "dashboard";
  return KNOWN_PAGES.has(seg) ? seg : "dashboard";
};

/** Extracts the product id segment for `/detail/<id>` URLs. */
export const detailIdFromPath = (pathname: string): string | null => {
  const parts = pathname.replace(/^\/+|\/+$/g, "").split("/");
  if (parts[0] !== "detail") return null;
  return parts[1] ? decodeURIComponent(parts[1]) : null;
};

/** Read the current URL slug synchronously. Use as the initial value of the
 * `page` state so the first commit already matches the URL — otherwise the
 * state→URL effect navigates `/` before URL→state hydrates, creating a
 * replaceState loop. */
export const initialPageFromUrl = (): string => {
  if (typeof window === "undefined") return "dashboard";
  return slugFromPath(window.location.pathname);
};

/**
 * Two-way sync between the monolith's internal `page` state and the URL
 * pathname (`/products`, `/checkout`, ...). Enables deep links, browser
 * back/forward, and shareable URLs. Replaces the old `?page=xxx` query.
 */
export function usePageUrlSync(
  page: string,
  setPage: (p: string) => void,
  opts?: {
    /** Current selected product id (used to build /detail/<id> URLs). */
    detailId?: string | number | null;
    /** Called whenever the URL exposes a `/detail/<id>` segment. */
    onDetailIdFromUrl?: (id: string | null) => void;
  },
) {
  const location = useLocation();
  const navigate = useNavigate();
  const hydratedRef = useRef(false);
  const detailId = opts?.detailId ?? null;
  const onDetailIdFromUrl = opts?.onDetailIdFromUrl;

  // URL → state (initial hydrate + back/forward)
  useEffect(() => {
    const fromUrl = slugFromPath(location.pathname);
    if (fromUrl !== page) setPage(fromUrl);
    if (fromUrl === "detail" && onDetailIdFromUrl) {
      onDetailIdFromUrl(detailIdFromPath(location.pathname));
    }
    hydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // state → URL
  useEffect(() => {
    // Skip until URL→state has hydrated, otherwise the default page state
    // ("dashboard") would clobber a deep link like /checkout on mount.
    if (!hydratedRef.current) return;
    // Extra guard: if `page` already matches the current URL slug, do
    // nothing. Prevents the mount-time race where this effect runs once
    // with the stale default "dashboard" before `setPage` from the
    // URL→state effect has been applied.
    const currentSlug = slugFromPath(location.pathname);
    const targetPath =
      page === "dashboard"
        ? "/"
        : page === "detail" && detailId != null && detailId !== ""
        ? `/detail/${encodeURIComponent(String(detailId))}`
        : `/${page}`;
    if (currentSlug === page && location.pathname === targetPath) return;
    if (location.pathname !== targetPath) {
      navigate(targetPath, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, detailId]);
}